import type { MaybeRefOrGetter } from 'vue'
import type { CommentableTypeEnum } from '~/shared/api'
import { toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  LikeableTypeEnum,
  useCommentsQuery,
  useCreateOneCommentMutation,
  useDeleteOneCommentMutation,
  useMarkCommentsReadMutation,
  useToggleLikeMutation,
  useUpdateOneCommentMutation,
} from '~/shared/api'
import { useConfirmDialog, useNotificationStore, usePolling } from '~/shared/lib'
import i18n from './CommentsPage.i18n.json'

const MAX_NESTING_DEPTH = 3

export function useCommentsThread(
  commentableId: MaybeRefOrGetter<string | undefined>,
  commentableType: MaybeRefOrGetter<CommentableTypeEnum>,
  options?: {
    enablePolling?: MaybeRefOrGetter<boolean>
  },
) {
  const { t } = useI18n(i18n)
  const { notify } = useNotificationStore()

  const commentText = ref('')
  const isLoading = ref(false)
  const activeCommentPopover = ref<string | null>(null)
  const editingCommentId = ref<string | null>(null)
  const editingCommentText = ref('')
  const replyingToCommentId = ref<string | null>(null)
  const replyText = ref('')

  const commentableIdRef = computed(() => toValue(commentableId))
  const commentableTypeRef = computed(() => toValue(commentableType))

  const { mutate: createComment } = useCreateOneCommentMutation()
  const { mutate: deleteComment } = useDeleteOneCommentMutation()
  const { mutate: updateComment } = useUpdateOneCommentMutation()
  const { mutate: toggleCommentLike } = useToggleLikeMutation()
  const { mutate: markCommentsRead } = useMarkCommentsReadMutation()

  // id уже отправленных в markCommentsRead (чтобы не дёргать мутацию на каждом поле опроса).
  const markedReadIds = new Set<string>()

  const { result: commentsResult, refetch: refetchComments } = useCommentsQuery(
    computed(() => ({
      filter: {
        commentableId: { eq: commentableIdRef.value || '' },
        commentableType: { eq: commentableTypeRef.value },
      },
      sorting: [{ field: 'createdAt', direction: 'ASC' as const }],
    })),
    {
      fetchPolicy: 'no-cache',
      enabled: computed(() => !!commentableIdRef.value),
    },
  )

  const allComments = computed(() => commentsResult.value?.comments?.nodes || [])
  const parentComments = computed(() => allComments.value.filter(comment => !comment.parentId))
  const hasComments = computed(() => allComments.value.length > 0)

  // Отметить прочитанными открытый тред: как только загрузились новые комментарии,
  // помечаем их read для текущего пользователя (идемпотентно). Так серверный
  // unreadCount обнулится, и бейдж «Сообщения» в задаче обновится при возврате.
  // Best-effort: ошибка не ломает тред; при неудаче id снимаются для повторной попытки.
  function markThreadRead() {
    const ids = allComments.value
      .map(comment => comment.id)
      .filter(id => !markedReadIds.has(id))
    if (ids.length === 0) {
      return
    }
    ids.forEach(id => markedReadIds.add(id))
    void Promise.resolve(markCommentsRead({ commentIds: ids })).catch(() => {
      ids.forEach(id => markedReadIds.delete(id))
    })
  }

  watch(allComments, markThreadRead, { immediate: true })

  function getReplies(parentId: string) {
    return allComments.value.filter(comment => comment.parentId === parentId)
  }

  function getCommentDepth(commentId: string, depth = 0): number {
    if (depth >= MAX_NESTING_DEPTH) {
      return depth
    }
    const comment = allComments.value.find(c => c.id === commentId)
    if (!comment?.parentId) {
      return depth
    }
    return getCommentDepth(comment.parentId, depth + 1)
  }

  function canReply(commentId: string): boolean {
    return getCommentDepth(commentId) < MAX_NESTING_DEPTH
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString)
    const timeString = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    })
    const dateString2 = date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    })
    return `${timeString}, ${dateString2}`
  }

  function getCreatorName(comment: { creator?: { firstName?: string | null, lastName?: string | null } | null }) {
    const creator = comment.creator
    if (creator?.firstName && creator?.lastName) {
      return `${creator.firstName} ${creator.lastName}`
    }
    return t('Пользователь')
  }

  function getCreatorAvatar(comment: { creator?: { media?: { avatar?: { DESKTOP?: { ORIGINAL?: string | null } | null } | null } | null } | null }) {
    const avatarUrl = comment.creator?.media?.avatar?.DESKTOP?.ORIGINAL
    return avatarUrl || '/avatar.webp'
  }

  async function handleSendComment() {
    if (!commentText.value.trim() || !commentableIdRef.value || isLoading.value) {
      return
    }
    isLoading.value = true
    try {
      const result = await createComment({
        input: {
          comment: {
            commentableType: commentableTypeRef.value,
            content: commentText.value.trim(),
            commentableId: commentableIdRef.value,
          },
        },
      })
      if (result?.data?.createOneComment) {
        notify({
          message: t('Комментарий успешно добавлен'),
          type: 'success',
        })
        commentText.value = ''
        await refetchComments()
      }
    }
    catch (error) {
      notify({
        message: `${t('Ошибка при добавлении комментария')}: ${error}`,
        type: 'error',
      })
    }
    finally {
      isLoading.value = false
    }
  }

  async function handleCommentLike(commentId: string, event?: Event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (!commentId) {
      return
    }
    try {
      const result = await toggleCommentLike({
        input: {
          likeableId: commentId,
          likeableType: LikeableTypeEnum.Comment,
        },
      })
      if (result?.data?.toggleLike) {
        notify({
          message: result.data.toggleLike.isLiked
            ? t('Комментарий добавлен в избранное')
            : t('Комментарий удален из избранного'),
          type: 'success',
        })
        await refetchComments()
      }
    }
    catch (error) {
      notify({
        message: `${t('Ошибка при изменении лайка')}: ${error}`,
        type: 'error',
      })
    }
  }

  function handleCommentMoreClick(commentId: string, event?: Event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    activeCommentPopover.value = activeCommentPopover.value === commentId ? null : commentId
  }

  function handleReplyClick(commentId: string) {
    replyingToCommentId.value = replyingToCommentId.value === commentId ? null : commentId
    replyText.value = ''
  }

  async function handleSendReply(parentId: string) {
    if (!replyText.value.trim() || !commentableIdRef.value || isLoading.value) {
      return
    }
    function getRootParent(commentId: string): string {
      const comment = allComments.value.find(c => c.id === commentId)
      if (!comment?.parentId) {
        return commentId
      }
      return getRootParent(comment.parentId)
    }
    const rootParentId = getRootParent(parentId)
    isLoading.value = true
    try {
      const result = await createComment({
        input: {
          comment: {
            commentableType: commentableTypeRef.value,
            content: replyText.value.trim(),
            commentableId: commentableIdRef.value,
            parentId: rootParentId,
          },
        },
      })
      if (result?.data?.createOneComment) {
        notify({
          message: t('Ответ успешно добавлен'),
          type: 'success',
        })
        replyingToCommentId.value = null
        replyText.value = ''
        await refetchComments()
      }
    }
    catch (error) {
      notify({
        message: `${t('Ошибка при добавлении ответа')}: ${error}`,
        type: 'error',
      })
    }
    finally {
      isLoading.value = false
    }
  }

  function handleCancelReply() {
    replyingToCommentId.value = null
    replyText.value = ''
  }

  function handleCommentEdit(commentId: string) {
    activeCommentPopover.value = null
    const comment = allComments.value.find(c => c.id === commentId)
    if (comment) {
      editingCommentId.value = commentId
      editingCommentText.value = comment.content
    }
  }

  async function handleSaveCommentEdit(commentId: string) {
    if (!editingCommentText.value.trim()) {
      notify({
        message: t('Комментарий не может быть пустым'),
        type: 'error',
      })
      return
    }
    try {
      const result = await updateComment({
        input: {
          id: commentId,
          update: {
            content: editingCommentText.value.trim(),
          },
        },
      })
      if (result?.data?.updateOneComment) {
        notify({
          message: t('Комментарий успешно обновлен'),
          type: 'success',
        })
        editingCommentId.value = null
        editingCommentText.value = ''
        await refetchComments()
      }
    }
    catch (error) {
      notify({
        message: `${t('Ошибка при обновлении комментария')}: ${error}`,
        type: 'error',
      })
    }
  }

  function handleCancelCommentEdit() {
    editingCommentId.value = null
    editingCommentText.value = ''
  }

  async function handleCommentDelete(commentId: string) {
    activeCommentPopover.value = null
    const isConfirmed = await useConfirmDialog({
      title: t('Удалить комментарий?'),
      text: t('Вы уверены, что хотите удалить этот комментарий? Это действие нельзя отменить.'),
      actions: [
        { text: t('Удалить'), type: 'resolve', variant: 'danger' },
        { text: t('Отмена'), type: 'reject', variant: 'dark' },
      ],
    }).open()
    if (!isConfirmed) {
      return
    }
    try {
      const result = await deleteComment({
        input: {
          id: commentId,
        },
      })
      if (result?.data?.deleteOneComment) {
        notify({
          message: t('Комментарий успешно удален'),
          type: 'success',
        })
        await refetchComments()
      }
    }
    catch (error) {
      notify({
        message: `${t('Ошибка при удалении комментария')}: ${error}`,
        type: 'error',
      })
    }
  }

  function closeCommentPopover() {
    activeCommentPopover.value = null
  }

  onMounted(() => {
    document.addEventListener('click', closeCommentPopover)
  })

  onUnmounted(() => {
    document.removeEventListener('click', closeCommentPopover)
  })

  usePolling(async () => {
    if (isLoading.value || editingCommentId.value || replyingToCommentId.value) {
      return
    }
    await refetchComments()
  }, {
    enabled: () => toValue(options?.enablePolling) === true,
  })

  return {
    t,
    commentText,
    isLoading,
    activeCommentPopover,
    editingCommentId,
    editingCommentText,
    replyingToCommentId,
    replyText,
    parentComments,
    hasComments,
    getReplies,
    canReply,
    formatDate,
    getCreatorName,
    getCreatorAvatar,
    handleSendComment,
    handleCommentLike,
    handleCommentMoreClick,
    handleReplyClick,
    handleSendReply,
    handleCancelReply,
    handleCommentEdit,
    handleSaveCommentEdit,
    handleCancelCommentEdit,
    handleCommentDelete,
  }
}
